// src/views/Configuracion.jsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE } from "../apiConfig";

// Catálogos que vas a manejar desde esta pantalla
const CATEGORIAS = [
  { id: "MUNICIPIOS", label: "Municipios" },
  { id: "OFICINAS", label: "Oficinas" },
  { id: "MODULOS", label: "Módulos" },
  { id: "TIPOS_EQUIPO", label: "Tipos de Equipo" },
  { id: "ESTADOS_EQUIPO", label: "Estados de Equipo" },
  { id: "THEME", label: "Tema Visual / Colores" },
];

export default function Configuracion() {
  const [categoria, setCategoria] = useState("MUNICIPIOS");
  const [valor, setValor] = useState("");
  const [items, setItems] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingSave, setLoadingSave] = useState(false);
  const [mensaje, setMensaje] = useState("");
  const [error, setError] = useState("");

  // =========================
  // CARGAR LISTA POR CATEGORÍA
  // =========================
  const cargarItems = async (cat) => {
    if (!cat) return;
    try {
      setLoadingList(true);
      setError("");
      setMensaje("");

      const res = await axios.get(`${API_BASE}/config`, {
        params: { categoria: cat },
      });

      setItems(res.data || []);
    } catch (e) {
      console.error("❌ Error cargando config:", e);
      setError("No se pudieron cargar los valores de configuración.");
    } finally {
      setLoadingList(false);
    }
  };

  // Ejecutar cuando cambia la categoría
  useEffect(() => {
    cargarItems(categoria);
  }, [categoria]);

  // =========================
  // GUARDAR NUEVO VALOR
  // =========================
  const guardar = async () => {
    if (!categoria || !valor.trim()) {
      setError("Debes seleccionar una categoría y capturar un valor.");
      setMensaje("");
      return;
    }

    try {
      setLoadingSave(true);
      setError("");
      setMensaje("");

      await axios.post(`${API_BASE}/config`, {
        categoria,
        valor: valor.trim(),
      });

      setMensaje("Valor guardado correctamente.");
      setValor("");
      await cargarItems(categoria);
    } catch (e) {
      console.error("❌ Error guardando config:", e);
      setError("No se pudo guardar el valor.");
    } finally {
      setLoadingSave(false);
    }
  };

  // =========================
  // RENDER
  // =========================
  return (
    <div className="max-w-6xl mx-auto px-6 py-8">
      {/* TÍTULO */}
      <header className="mb-8">
        <p className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-1">
          Panel de administración
        </p>
        <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight">
          Configuración dinámica del sistema
        </h1>
        <p className="text-sm text-slate-500 mt-2 max-w-2xl">
          Administra catálogos clave (municipios, oficinas, módulos, tipos de equipo, etc.)
          desde un solo lugar, sin modificar el código.
        </p>
      </header>

      {/* MENSAJES */}
      {(mensaje || error) && (
        <div className="mb-6">
          {mensaje && (
            <div className="mb-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-800">
              {mensaje}
            </div>
          )}
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-800">
              {error}
            </div>
          )}
        </div>
      )}

      {/* LAYOUT */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        {/* FORMULARIO */}
        <section className="lg:col-span-1">
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">
              Nuevo valor de configuración
            </h2>

            {/* Categoría */}
            <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">
              Categoría
            </label>
            <select
              className="w-full mb-4 rounded-xl border border-slate-300 bg-slate-50 px-3 py-2 text-sm
                         focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white"
              value={categoria}
              onChange={(e) => setCategoria(e.target.value)}
            >
              {CATEGORIAS.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </select>

            {/* Valor */}
            <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">
              Valor
            </label>
            <textarea
              rows={3}
              className="w-full rounded-xl border border-slate-300 bg-slate-50 px-3 py-2 text-sm
                         focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white mb-4"
              placeholder="Ejemplo: Benito Juárez / Módulo 01 / HP P22va G4 / #0F172A"
              value={valor}
              onChange={(e) => setValor(e.target.value)}
            ></textarea>

            {/* Botón */}
            <button
              onClick={guardar}
              disabled={loadingSave}
              className="w-full inline-flex items-center justify-center gap-2 rounded-xl 
                         bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 
                         text-white text-sm font-semibold py-2.5 shadow-sm"
            >
              {loadingSave ? (
                <>
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                  Guardando...
                </>
              ) : (
                <>
                  <span className="text-lg">➕</span>
                  Guardar en {categoria}
                </>
              )}
            </button>
          </div>
        </section>

        {/* LISTADO */}
        <section className="lg:col-span-2">
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6">
            {/* Header listado */}
            <div className="flex items-center justify-between mb-4 gap-3">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">
                  Valores registrados
                </h2>
                <p className="text-xs text-slate-500">
                  Lista de valores para la categoría seleccionada.
                </p>
              </div>

              <button
                onClick={() => cargarItems(categoria)}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-300 
                           bg-slate-50 hover:bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-700"
              >
                <span className="w-3 h-3 border-2 border-slate-500 border-t-transparent rounded-full animate-spin"></span>
                Refrescar
              </button>
            </div>

            {/* Loader */}
            {loadingList && (
              <div className="flex items-center justify-center h-32">
                <div className="w-8 h-8 border-4 border-slate-200 border-t-indigo-500 rounded-full animate-spin"></div>
              </div>
            )}

            {/* Sin datos */}
            {!loadingList && items.length === 0 && (
              <div className="border border-dashed border-slate-200 rounded-xl p-6 text-center">
                <p className="text-sm text-slate-500 mb-1">
                  No hay valores registrados para esta categoría.
                </p>
                <p className="text-xs text-slate-400">
                  Agrega el primer valor desde el panel de la izquierda.
                </p>
              </div>
            )}

            {/* Tabla */}
            {!loadingList && items.length > 0 && (
              <div className="mt-3 overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-slate-900 text-white text-xs uppercase tracking-wide">
                      <th className="px-3 py-2 text-left">Valor</th>
                      <th className="px-3 py-2 text-left hidden md:table-cell">SK</th>
                      <th className="px-3 py-2 text-left hidden md:table-cell">Fecha registro</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => (
                      <tr
                        key={item.SK}
                        className="border-b border-slate-100 hover:bg-slate-50"
                      >
                        <td className="px-3 py-2 align-top">{item.value}</td>
                        <td className="px-3 py-2 align-top text-xs text-slate-500 hidden md:table-cell">
                          {item.SK}
                        </td>
                        <td className="px-3 py-2 align-top text-xs text-slate-500 hidden md:table-cell">
                          {item.createdAt
                            ? new Date(item.createdAt).toLocaleString("es-MX", {
                                year: "numeric",
                                month: "2-digit",
                                day: "2-digit",
                                hour: "2-digit",
                                minute: "2-digit",
                              })
                            : "N/D"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
